import { AxiosError } from 'axios';

// Types
export interface CollectionStats {
  total: {
    volume: number;
    sales: number;
    average_price: number;
    num_owners: number;
    market_cap: number;
    floor_price: number;
    floor_price_symbol: string;
  };
  intervals: {
    interval: string;
    volume: number;
    volume_diff: number;
    volume_change: number;
    sales: number;
    sales_diff: number;
    average_price: number;
  }[];
}

export interface Listing {
  order_hash: string;
  price: {
    current: {
      currency: string;
      decimals: number;
      value: string;
    };
  };
}

export interface CollectionEvent {
  event_type: string;
  event_timestamp: number;
  payment?: {
    quantity: string;
    token_address: string;
    decimals: number;
    symbol: string;
  };
  nft?: {
    identifier: string;
    collection: string;
    contract: string;
  };
  buyer?: string;
  seller?: string;
}

export interface CollectionDetails {
  collection: string;
  name: string;
  description: string;
  image_url: string;
  banner_image_url: string;
  total_supply: number;
  editors: string[];
  created_date: string;
}

const OPENSEA_API_KEY = '2aa8487849794ffea6a59027b78c1ee8';
const BASE_URL = 'https://api.opensea.io/api/v2';

const headers = {
  'accept': 'application/json',
  'x-api-key': OPENSEA_API_KEY
};

export class OpenSeaService {
  static async getCollectionDetails(collectionSlug: string): Promise<CollectionDetails> {
    try {
      const response = await fetch(`${BASE_URL}/collections/${collectionSlug}`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenSea API Error (${response.status}): ${errorText || response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch collection details:', error);
      throw error;
    }
  }

  static async getCollectionStats(collectionSlug: string): Promise<CollectionStats> {
    try {
      const response = await fetch(`${BASE_URL}/collections/${collectionSlug}/stats`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenSea API Error (${response.status}): ${errorText || response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch collection stats:', error);
      throw error;
    }
  }

  static async getBestListings(collectionSlug: string): Promise<Listing[]> {
    try {
      const response = await fetch(`${BASE_URL}/listings/collection/${collectionSlug}/best`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenSea API Error (${response.status}): ${errorText || response.statusText}`);
      }
      
      const data = await response.json();
      return data.listings || [];
    } catch (error) {
      console.error('Failed to fetch best listings:', error);
      throw error;
    }
  }

  static async getAllListings(collectionSlug: string): Promise<Listing[]> {
    try {
      const response = await fetch(`${BASE_URL}/listings/collection/${collectionSlug}/all`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenSea API Error (${response.status}): ${errorText || response.statusText}`);
      }
      
      const data = await response.json();
      return data.listings || [];
    } catch (error) {
      console.error('Failed to fetch all listings:', error);
      throw error;
    }
  }

  static async getCollectionEvents(collectionSlug: string): Promise<CollectionEvent[]> {
    try {
      const response = await fetch(`${BASE_URL}/events/collection/${collectionSlug}`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenSea API Error (${response.status}): ${errorText || response.statusText}`);
      }
      
      const data = await response.json();
      return data.asset_events || [];
    } catch (error) {
      console.error('Failed to fetch collection events:', error);
      throw error;
    }
  }

  // Helper method to calculate additional metrics
  static calculateAdditionalMetrics(stats: CollectionStats, events: CollectionEvent[]) {
    // Filter only sales events
    const salesEvents = events.filter(event => event.event_type === 'sale');
    
    // Calculate unique buyers and sellers
    const uniqueBuyers = new Set(salesEvents.map(event => event.buyer).filter(Boolean)).size;
    const uniqueSellers = new Set(salesEvents.map(event => event.seller).filter(Boolean)).size;
    
    // Calculate average price change over time periods
    const averagePriceChange = stats.intervals.length > 1 
      ? (stats.intervals[0].average_price - stats.intervals[1].average_price) / stats.intervals[1].average_price * 100 
      : 0;
    
    // Calculate sales volume change
    const salesVolumeChange = stats.intervals.length > 1 
      ? (stats.intervals[0].volume - stats.intervals[1].volume) / stats.intervals[1].volume * 100 
      : 0;
    
    // Calculate liquidity (ratio of traded to total supply)
    const liquidity = stats.total.sales > 0 ? (stats.total.sales / 10000) * 100 : 0; // Assuming 10,000 total supply
    
    return {
      uniqueBuyers,
      uniqueSellers,
      averagePriceChange,
      salesVolumeChange,
      liquidity
    };
  }
} 